import { AlertTriangle, ArrowUpRight, Check, ChevronDown, Copy, Info, Lightbulb, Link2, MessageSquareWarning, OctagonAlert } from "lucide-react";
import { useEffect, useState, type ComponentProps, type ReactNode, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { blockDir, type HastLike } from "@/lib/bidi.ts";
import { cn } from "@/lib/cn.ts";
import { copyText } from "@/lib/uid.ts";
import { useImageViewer } from "@/ui/ImageViewer.tsx";
import { CALLOUTS, hastText, shortUrl, tableTsv, type CalloutKind } from "./md.ts";

type Node = HastLike & { properties?: Record<string, unknown> };

/** A small copy button for the reader's tools in an answer (marked so a copied answer leaves them out). */
export function CopyButton({ text, label, className }: { text: () => string | Promise<void>; label: string; className?: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      data-md-ui
      title={done ? t("common.copied") : label}
      aria-label={done ? t("common.copied") : label}
      className={cn("grid size-7 place-items-center rounded-md text-fg-muted hover:bg-surface-2 hover:text-fg", className)}
      onClick={async (e) => {
        e.stopPropagation();
        const v = text();
        if (typeof v === "string") await copyText(v);
        else await v;
        setDone(true);
        setTimeout(() => setDone(false), 1500);
      }}
    >
      {done ? <Check className="size-3.5 text-success" /> : <Copy className="size-3.5" />}
    </button>
  );
}

// ---------------------------------------------------------------- tables

/** A table in its own sideways scroller (the page never drifts), with a header, banded rows and a copy button. */
export function MdTable({ node, children }: { node?: Node | undefined; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    // the table reads in its own language's direction (columns from the right for Arabic), whatever the page's
    <div className="md-table group/table" dir={blockDir(node) ?? "auto"}>
      <div className="md-table-scroll">
        <table>{children}</table>
      </div>
      <CopyButton text={() => tableTsv(node)} label={t("chat.md.copyTable")} className="md-tool" />
    </div>
  );
}

/** Each cell reads in its own language's direction, like the paragraphs. */
export function MdCell({ node, as: Tag, ...rest }: { node?: Node | undefined; as: "td" | "th" } & ComponentProps<"td">) {
  return <Tag {...rest} dir={blockDir(node) ?? "auto"} />;
}

// ---------------------------------------------------------------- callouts

const CALLOUT_ICON: Record<CalloutKind, typeof Info> = { note: Info, tip: Lightbulb, important: MessageSquareWarning, warning: AlertTriangle, caution: OctagonAlert };

/** A quote, or a GitHub alert ([!NOTE], [!TIP], [!IMPORTANT], [!WARNING], [!CAUTION]) as a coloured box. */
export function MdQuote({ node, children }: { node?: Node | undefined; children?: ReactNode }) {
  const { t } = useTranslation();
  const kind = node?.properties?.dataCallout as CalloutKind | undefined;
  const dir = blockDir(node) ?? "auto";
  if (!kind || !CALLOUTS.includes(kind)) return <blockquote dir={dir}>{children}</blockquote>;
  const Icon = CALLOUT_ICON[kind];
  return (
    <div dir={dir} className={cn("md-callout", `md-callout-${kind}`)}>
      <div className="md-callout-title"><Icon className="size-4" /> {t(`chat.md.callout.${kind}`)}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- links

/** A bare address shown as a short chip (host, then the path), so a long URL doesn't break across lines. */
export function BareLink({ href, anchor }: { href: string; anchor: (children: ReactNode, className: string) => ReactNode }) {
  const s = shortUrl(href);
  if (!s) return <>{anchor(href, "")}</>;
  return (
    <>
      {anchor(
        <>
          <Link2 className="size-3.5 shrink-0 opacity-70" />
          <span className="shrink-0 whitespace-nowrap font-medium">{s.host}</span>
          {s.path && <span className="min-w-0 truncate opacity-70">{s.path}</span>}
        </>,
        "md-link-chip",
      )}
    </>
  );
}

/** The arrow after a link that opens outside the platform. */
export function ExternalMark() {
  return <ArrowUpRight className="md-ext" aria-hidden />;
}

// ---------------------------------------------------------------- code

type Hljs = typeof import("highlight.js").default;
let hljsP: Promise<Hljs> | null = null;
const loadHljs = () => (hljsP ??= import("highlight.js/lib/common").then((m) => m.default));
const FOLD_LINES = 30;
const MAX_HIGHLIGHT = 40_000;

/** A fenced block: the language on top, a copy button, colours (loaded on first use), long ones folded. Mermaid
 *  diagrams and display maths get their own renderers. */
export function MdPre({ node }: { node?: Node | undefined }) {
  const code = node?.children?.find((c) => c.tagName === "code") as Node | undefined;
  const cls = code?.properties?.className;
  const classes = Array.isArray(cls) ? (cls as string[]) : [];
  const lang = classes.find((c) => c.startsWith("language-"))?.slice(9).toLowerCase() ?? "";
  const text = hastText(code).replace(/\n$/, "");
  if (lang === "math") return <MathTex tex={text} display />;
  if (lang === "mermaid") return <Mermaid code={text} />;
  return <CodeBlock code={text} lang={lang} />;
}

export function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const { t } = useTranslation();
  const [html, setHtml] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const lines = code.split("\n").length;
  useEffect(() => {
    if (!lang || code.length > MAX_HIGHLIGHT) { setHtml(null); return; }
    let live = true;
    // short pause so a block still streaming in is coloured once, not on every token
    const timer = setTimeout(() => {
      void loadHljs().then((hljs) => {
        if (!live || !hljs.getLanguage(lang)) return;
        // highlight.js escapes the code it returns; only its own <span class="hljs-…"> markup is added
        setHtml(hljs.highlight(code, { language: lang, ignoreIllegals: true }).value);
      });
    }, 150);
    return () => { live = false; clearTimeout(timer); };
  }, [code, lang]);
  const folded = lines > FOLD_LINES && !open;
  return (
    <div className="md-code" dir="ltr">
      <div className="md-code-bar" data-md-ui>
        <span className="font-mono">{lang || t("chat.md.code")}</span>
        <CopyButton text={() => code} label={t("chat.md.copyCode")} className="ms-auto size-6" />
      </div>
      <pre className={cn(folded && "md-code-folded")}>
        {html !== null ? <code className="hljs" dangerouslySetInnerHTML={{ __html: html }} /> : <code>{code}</code>}
      </pre>
      {lines > FOLD_LINES && (
        <button type="button" data-md-ui className="md-code-more" onClick={() => setOpen(!open)}>
          <ChevronDown className={cn("size-3.5 transition-transform", open && "rotate-180")} />
          {open ? t("chat.showLess") : t("chat.md.moreLines", { count: lines - FOLD_LINES })}
        </button>
      )}
    </div>
  );
}

/** Inline code; inline maths ($$…$$ inside a line) is typeset. */
export function MdCode({ node, className, children }: { node?: Node | undefined; className?: string | undefined; children?: ReactNode }) {
  if (className?.includes("math-inline")) return <MathTex tex={hastText(node)} />;
  return <code className={className}>{children}</code>;
}

// ---------------------------------------------------------------- maths

type Katex = typeof import("katex").default;
let katexP: Promise<Katex> | null = null;
const loadKatex = () => (katexP ??= Promise.all([import("katex"), import("katex/dist/katex.min.css")]).then(([m]) => m.default));

/** TeX typeset by KaTeX, loaded on first use. KaTeX's own escaping applies and \href/\url-like commands stay off
 *  (trust: false), so an answer can't slip markup in through a formula. */
export function MathTex({ tex, display = false }: { tex: string; display?: boolean }) {
  const [html, setHtml] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void loadKatex().then((k) => {
      if (live) setHtml(k.renderToString(tex, { displayMode: display, throwOnError: false, trust: false, strict: "ignore", output: "htmlAndMathml" }));
    });
    return () => { live = false; };
  }, [tex, display]);
  const Tag = display ? "div" : "span";
  if (html === null) return <Tag dir="ltr" className={cn("font-mono text-fg-muted", display && "md-math")}>{tex}</Tag>;
  return <Tag dir="ltr" className={cn(display && "md-math")} dangerouslySetInnerHTML={{ __html: html }} />;
}

// ---------------------------------------------------------------- diagrams

type MermaidApi = typeof import("mermaid").default;
let mermaidP: Promise<MermaidApi> | null = null;
const loadMermaid = () => (mermaidP ??= import("mermaid").then((m) => m.default));
let mermaidSeq = 0;
// The diagram is shown as an image, which can't use the page's web font (IBM Plex): Mermaid sizes the boxes with a
// font the image has too, so labels fit inside them.
const MERMAID_FONT = "system-ui, -apple-system, 'Segoe UI', 'Geeza Pro', Tahoma, Arial, sans-serif";
const ARABIC = /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/;

/** Arabic labels read right to left. Mermaid writes each line as a <tspan> of word <tspan>s, and browsers ignore an SVG
 *  direction there, so each Arabic line is wrapped in the Unicode right-to-left embedding marks (RLE … PDF). */
export function rtlLabels(svg: string): string {
  if (!ARABIC.test(svg) || typeof DOMParser === "undefined") return svg;
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  if (doc.querySelector("parsererror")) return svg;
  for (const t of doc.querySelectorAll("text")) {
    if (!ARABIC.test(t.textContent ?? "")) continue;
    const lines = t.querySelectorAll(":scope > tspan");
    for (const line of lines.length ? [...lines] : [t]) {
      const texts: Text[] = [];
      const walker = doc.createTreeWalker(line, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) texts.push(n as Text);
      if (!texts.length) continue;
      texts[0]!.data = "\u202B" + texts[0]!.data;
      texts[texts.length - 1]!.data += "\u202C";
    }
  }
  return new XMLSerializer().serializeToString(doc);
}

/**
 * A Mermaid diagram, drawn by Mermaid (loaded on first use) and shown as an image: an SVG inside <img> runs no
 * script and loads nothing, whatever the diagram's text holds. While it is still streaming in, or if it doesn't
 * parse, the code is shown instead.
 */
export function Mermaid({ code }: { code: string }) {
  const { t } = useTranslation();
  const viewImages = useImageViewer();
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const dark = typeof document !== "undefined" && document.documentElement.dataset.theme === "dark";
  useEffect(() => {
    let live = true;
    const timer = setTimeout(() => {
      void loadMermaid().then(async (m) => {
        m.initialize({ startOnLoad: false, securityLevel: "strict", theme: dark ? "dark" : "default", htmlLabels: false, flowchart: { htmlLabels: false }, fontFamily: MERMAID_FONT });
        try {
          const { svg } = await m.render(`md-mermaid-${++mermaidSeq}`, code);
          if (live) { setSrc(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(rtlLabels(svg))}`); setFailed(false); }
        } catch {
          if (live) setFailed(true);
        }
      });
    }, 400);
    return () => { live = false; clearTimeout(timer); };
  }, [code, dark]);
  if (!src || failed) return <CodeBlock code={code} lang="mermaid" />;
  return (
    <div className="md-diagram">
      <button type="button" className="block w-full cursor-zoom-in" onClick={() => viewImages([{ src, name: "diagram.svg" }])} aria-label={t("chat.md.diagram")}>
        <img src={src} alt={t("chat.md.diagram")} className="mx-auto max-h-[28rem] max-w-full" />
      </button>
      <CopyButton text={() => code} label={t("chat.md.copyCode")} className="md-tool" />
    </div>
  );
}

// ---------------------------------------------------------------- the whole answer

/** Copies an answer as it reads: formatted (HTML) for documents and mail, plain text for everything else. The
 *  reader's tools (copy buttons, fold toggles) are left out. Falls back to the Markdown source. */
export async function copyRendered(el: HTMLElement | null, markdown: string): Promise<void> {
  if (!el) return copyText(markdown);
  const clone = el.cloneNode(true) as HTMLElement;
  clone.querySelectorAll("[data-md-ui]").forEach((n) => n.remove());
  // innerText needs layout: read it from the cleaned copy, laid out off screen for a moment
  clone.style.cssText = "position:fixed;left:-99999px;top:0;width:" + el.clientWidth + "px";
  document.body.appendChild(clone);
  const plain = (clone.innerText || markdown).trim();
  clone.remove();
  try {
    if (typeof ClipboardItem === "undefined" || !navigator.clipboard?.write) throw new Error("no rich clipboard");
    await navigator.clipboard.write([new ClipboardItem({ "text/html": new Blob([clone.innerHTML], { type: "text/html" }), "text/plain": new Blob([plain], { type: "text/plain" }) })]);
  } catch {
    await copyText(plain);
  }
}

/** The copy button under an answer. */
export function CopyAnswer({ target, markdown }: { target: RefObject<HTMLDivElement | null>; markdown: string }) {
  const { t } = useTranslation();
  return (
    <div className="md-answer-tools" data-md-ui>
      <CopyButton text={() => copyRendered(target.current, markdown)} label={t("chat.md.copyAnswer")} />
    </div>
  );
}

